/**
 * API client for Google Meet / OAuth integration
 *
 * Exports:
 *  - getAuthSetupUrl()  -> GET /auth/setup            (returns { authUrl, instructions })
 *  - sendAuthCode(code, state) -> POST /auth/callback (accepts { code, state })
 *  - getAuthStatus()    -> GET /auth/status
 *  - revokeAuthToken()  -> POST /auth/revoke
 *
 * Uses axios and reads base URL from REACT_APP_API_BASE_URL.
 *
 * Notes:
 *  - If your backend uses cookies/sessions, enable `withCredentials`.
 *  - Error responses are normalized to have a `.data` and `.status` shape similar to axios.
 */

import axios from "axios";
// Prefer environment override for API base; fallback to the api constant
const rawBase = import.meta.env.VITE_APP_API_BASE_URL 
const API_BASE = (rawBase || "").replace(/\/+$/g, ""); // remove trailing slash if any
const GOOGLE_API_BASE = `${API_BASE}/api/google`;

/**
 * Create axios instance scoped for google auth APIs
 */
const apiClient = axios.create({
  baseURL: GOOGLE_API_BASE,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // If your backend sets http-only cookies for auth, uncomment the next line:
  // withCredentials: true,
});

// Response interceptor: normalize error shape and optionally handle global errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // If it's an axios error with response, return the response object so callers get .data and .status
    if (error && error.response) {
      return Promise.reject(error.response);
    }
    // Otherwise, normalize to an object with .data.message and status 0 (network)
    return Promise.reject({
      data: { message: error.message || "Network error" },
      status: 0,
    });
  },
);

/**
 * GET /auth/setup
 * Returns an authorization URL that the frontend should open in a popup.
 * Expected response shape:
 * { message: string, authUrl: string, instructions?: string }
 */
/**
 * GET /auth/setup
 * Accepts optional params object which will be sent as query params.
 * Example: getAuthSetupUrl({ email: 'a@b.com', clinicId: 12 })
 */
export const getAuthSetupUrl = (params = {}) => apiClient.get("/auth/setup", { params });

/**
 * POST /auth/callback
 * Send the authorization code received from Google to the backend to exchange for tokens.
 * Request body: { code: string, state?: string }
 * Expected response shape:
 * { message, tokenSaved, hasRefreshToken, email, ... }
 */
// Send authorization code to backend. Optionally include `email` to associate
// the token with a clinic/admin email.
export const sendAuthCode = (code, state = null, email = null, clinicId = null) =>
  apiClient.post("/auth/callback", { code, state, email, clinicId });

/**
 * GET /auth/status
 * Returns the current status for the connected Google account (if any).
 * Accepts optional params object which will be sent as query params.
 * Example: getAuthStatus({ email: 'a@b.com' })
 * Expected response shape:
 * {
 *   authenticated: boolean,
 *   hasRefreshToken: boolean,
 *   expiresIn: number,         // seconds
 *   expiryDate: string|number, // optional
 *   isExpiringSoon: boolean,
 *   requiresReauth: boolean,
 *   reason?: string
 * }
 */
export const getAuthStatus = (params = {}) => apiClient.get("/auth/status", { params });

/**
 * POST /auth/revoke
 * Instructs backend to revoke tokens and unlink the account.
 * Expected response shape: { message: string, success: boolean }
 */
export const revokeAuthToken = () => apiClient.post("/auth/revoke");

/**
 * GET /auth/accounts
 * Returns list of authenticated Google accounts (see integration guide)
 * Expected response shape: { accounts: [ { email, primary, ... } ] }
 */
export const getAuthAccounts = () => apiClient.get("/auth/accounts");

export default apiClient;
